import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const headingFont = { fontFamily: "'Sustainability', sans-serif" };
const bodyFont = { fontFamily: 'Sustainability, sans-serif', fontWeight: 300 };

const stories = [
  {
    id: 1,
    title: 'STRENGTHENING OUR COMMUNITY AND BEYOND',
    description: `In Mongolia's Gobi region, many families live in remote and challenging conditions, where even
      the simplest comforts are rare. Schools, dormitories, hospitals, and kindergartens often struggle
      to offer enough bedding, educational materials, and everyday essentials. This makes it hard to
      provide the safe, supportive environment that every child and family deserves.
      To help, we partnered with Mongolia Aid in Australia and created the Gobi Revival Fund.
      Working together, we help herding families and local groups gain the resources they need to
      improve daily life. Whether it's educational materials, electronic devices for learning, washing
      machines, or warm bedding for schools and hospitals, we're proud to support our community's
      future—one small step at a time.`,
    image:
      'https://d1f6qhhrbg3j8a.cloudfront.net/img/270653/original/17D_0813.jpg',
    href: '/journal',
  },
  {
    id: 2,
    title: 'REVIVING HOPE AFTER HARD WINTERS',
    description: `In 2000 and 2001, Mongolia faced one of its harshest winters—what we call a dzud. Extreme
      cold, deep snow, and ice made grazing impossible. In the Gobi region, families lost more than
      80 percent of their livestock. The impact was felt for years, as herding families lost not only
      their income, but also their way of life and the legacy of generations.
      Wanting to do more than offer short-term aid, we focused on helping families rebuild for the
      long term. With support from our partners, we gifted 1,600 goats to 40 families, choosing
      healthy animals from regions unaffected by the dzud. This was about restoring hope and
      helping families begin again, step by step.
      Now, our herds have grown strong again, with more than 200,000 animals thriving in the
      region. Families have found stability and new beginnings. Many herders have even earned
      recognition for their resilience and hard work. Today, our region is celebrated for producing
      some of Mongolia's finest white cashmere—valued for its quality, sustainability, and the story
      behind every fiber.`,
    image:
      'https://d1f6qhhrbg3j8a.cloudfront.net/img/270642/original/17D_9970.jpg',
    href: '/journal',
  },
  {
    id: 3,
    title: 'BRINGING WATER, GROWING FUTURES',
    description: `After Mongolia moved to a market economy, herders became owners of their livestock. This
      brought new freedoms, but also new challenges. Access to animal healthcare became harder,
      leaving many communities without the support needed to keep their herds healthy. Over time,
      herders and their animals struggled, and the quality of the fibers they relied on was at risk.
      In 2015, with the help of local and international partners, we started a comprehensive animal
      care program—the first in our region in over 20 years. We provided free veterinary services to
      our community, including vaccinations, washing, sanitation, and parasite control for goats and
      sheep. Our goal was to protect animal welfare while helping herders keep their livelihoods
      secure and sustainable.
      Healthy animals mean stronger herds and more stable lives for families. Better animal care also
      leads to higher-quality natural fibers, so every product we make is responsibly sourced from the
      very start. This project reflects our ongoing promise: to care for the animals, support our land,
      and stand with the communities at the heart of everything we do.`,
    image:
      'https://d1f6qhhrbg3j8a.cloudfront.net/img/270644/original/17D_0111.jpg',
    href: '/journal',
  },
  {
    id: 4,
    title: 'Water Supply Project',
    description: `In the Gobi—whose very name means "waterless land"—clean drinking water is precious and
      rare. Many families rely on shallow wells or open water sources shared with livestock, which
      can affect health, especially for young children.
      Wanting to make a lasting difference, we funded a clean water initiative in Bayangobi village.
      Working closely with the community, we helped bring a natural underground water source
      safely into the village, creating new access points for safe, reliable water.
      Clean water has transformed daily life. It's improved health, strengthened families, and opened
      the door to new opportunities. Today, the community is growing more self-sufficiently—with
      small greenhouses, tree planting, and shared facilities all made possible by this essential
      resource.`,
    image:
      'https://d1f6qhhrbg3j8a.cloudfront.net/img/270646/original/ELBE2833.JPG',
    href: '/journal',
  },
];

export default function InsideJournalSlider() {
  const [activeIndex, setActiveIndex] = useState(4);
  const [visibleCount, setVisibleCount] = useState(4);
  const [isResetting, setIsResetting] = useState(false);
  const [selectedStory, setSelectedStory] = useState(null);
  const total = stories.length;

  const truncateWords = (text, maxWords = 50) => {
    const clean = String(text || "").replace(/\s+/g, " ").trim();
    const words = clean.split(" ").filter(Boolean);
    if (words.length <= maxWords) return clean;
    return `${words.slice(0, maxWords).join(" ")} ...`;
  };

  useEffect(() => {
    const setByViewport = () => {
      setVisibleCount(window.innerWidth < 768 ? 1 : 4);
    };
    setByViewport();
    window.addEventListener('resize', setByViewport);
    return () => window.removeEventListener('resize', setByViewport);
  }, []);

  useEffect(() => {
    // Reset track to real first block when layout changes.
    setActiveIndex(visibleCount);
    setIsResetting(true);
    const id = window.setTimeout(() => setIsResetting(false), 20);
    return () => window.clearTimeout(id);
  }, [visibleCount]);

  const extendedStories = useMemo(() => {
    const leftClones = stories.slice(-visibleCount);
    const rightClones = stories.slice(0, visibleCount);
    return [...leftClones, ...stories, ...rightClones];
  }, [visibleCount]);

  const handleTransitionEnd = () => {
    const minRealIndex = visibleCount;
    const maxRealIndex = visibleCount + total - 1;

    if (activeIndex > maxRealIndex) {
      const overflow = activeIndex - maxRealIndex - 1;
      setIsResetting(true);
      setActiveIndex(minRealIndex + overflow);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsResetting(false));
      });
    } else if (activeIndex < minRealIndex) {
      const underflow = minRealIndex - activeIndex - 1;
      setIsResetting(true);
      setActiveIndex(maxRealIndex - underflow);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsResetting(false));
      });
    }
  };

  const next = () => setActiveIndex((prev) => prev + 1);
  const prev = () => setActiveIndex((prev) => prev - 1);
  const openStoryModal = (story) => setSelectedStory(story);
  const closeStoryModal = () => setSelectedStory(null);

  useEffect(() => {
    if (!selectedStory) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeStoryModal();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [selectedStory]);

  return (
    <section className="w-full px-4 md:px-0 mt-12 md:mt-16 mb-12 md:mb-16">
      <div className="max-w-[1780px] mx-auto p-5 md:p-0 lg:p-0">
        <div className="flex items-center justify-center mb-5 md:mb-6">
          <h2 className="text-[24px] md:text-[30px] text-[#1e1c1a] text-center" style={headingFont}>
            Our journal
          </h2>
        </div>

        <div className="relative px-4 md:px-8 lg:px-10">
          <button
            type="button"
            onClick={prev}
            aria-label="Previous slide"
            className="absolute left-0 md:left-1 top-[42%] -translate-y-1/2 z-20 h-[100px] w-10 flex items-center justify-center text-[#3d372f] hover:text-[#1f1a16] transition-colors"
          >
            <svg viewBox="0 0 24 24" className="h-[72px] w-[28px]" fill="none" stroke="currentColor" aria-hidden>
              <path d="M15 4L8 12L15 20" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next slide"
            className="absolute right-0 md:right-1 top-[42%] -translate-y-1/2 z-20 h-[100px] w-10 flex items-center justify-center text-[#3d372f] hover:text-[#1f1a16] transition-colors"
          >
            <svg viewBox="0 0 24 24" className="h-[72px] w-[28px]" fill="none" stroke="currentColor" aria-hidden>
              <path d="M9 4L16 12L9 20" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className="overflow-hidden">
          <div
            className={`flex ${isResetting ? 'transition-none' : 'transition-transform duration-700 ease-out'}`}
            style={{ transform: `translateX(-${(activeIndex * 100) / visibleCount}%)` }}
            onTransitionEnd={handleTransitionEnd}
          >
            {extendedStories.map((story, i) => (
            <article key={`${story.id}-${i}`} className="flex flex-col px-4 md:px-5" style={{ flex: `0 0 ${100 / visibleCount}%` }}>
              <button
                type="button"
                onClick={() => openStoryModal(story)}
                className="block w-full overflow-hidden bg-[#ebe8e1] text-left"
              >
                <img
                  src={story.image}
                  alt={story.title}
                  className="w-full aspect-[3/4] object-cover hover:scale-[1.02] transition-transform duration-300"
                  loading="lazy"
                />
              </button>
              <div className="pt-4">
                <h3
                  className="text-[16px] lg:text-[21px] leading-[1.3] text-[#2c2721] mb-2"
                  style={headingFont}
                >
                  {story.title}
                </h3>
                <p
                  className="text-[13px] lg:text-[16px] leading-[1.5] text-[#5f574c]"
                  style={bodyFont}
                >
                  {truncateWords(story.description, 28)}
                </p>
              </div>
            </article>
          ))}
          </div>
          </div>
        </div>
      </div>

      {selectedStory && (
        <div
          className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4"
          onClick={closeStoryModal}
        >
          <div
            className="bg-white w-full max-w-[920px] max-h-[90vh] overflow-auto rounded-sm shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="relative">
              <img
                src={selectedStory.image}
                alt={selectedStory.title}
                className="w-full h-[260px] md:h-[360px] object-cover"
              />
              <button
                type="button"
                onClick={closeStoryModal}
                aria-label="Close details popup"
                className="absolute right-3 top-3 h-8 w-8 rounded-full bg-white/90 text-[#2c2520] hover:bg-white"
              >
                ✕
              </button>
            </div>
            <div className="p-5 md:p-7">
              <h3 className="text-[22px] md:text-[28px] text-[#1f1a16] mb-4" style={headingFont}>
                {selectedStory.title}
              </h3>
              <p className="text-[14px] lg:text-[16px] leading-[1.65] text-[#544d44] whitespace-pre-line" style={bodyFont}>
                {selectedStory.description}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}